import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { isNil } from 'ramda';

import {
  initialValues,
  validationSchema,
  EMAIL,
  MIXPANEL_COHORT_ID,
  FORM_FIELDS,
} from '../../../../forms/messages/sendMessageForm';
import Flash from '../../../../components/forms/Flash';
import MessagePresenter from '../../../../presenters/MessagePresenter';
import { apiErrorsToFormik } from '../../../../utils/formUtils';
import TextInput from '../../../../components/TextInput';
import { FLASH_TYPES } from '../../../../constants/flashTypes';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import Group from '../../../../components/Group';
import FormHeader from '../../../../components/FormHeader';
import { useFetchStatus } from '../../../../utils/fetchStatusUtils';

import MessageSelect from './components/MessageSelect';
import EmailInput from './components/EmailInput';
import RecepientSelect from './components/RecepientSelect';

const SendMessageForm = (props) => {
  const { messages, loadingStatus, onSendMessage, apiErrors } = props;
  const { isFulfilled } = useFetchStatus(loadingStatus);
  const [success, setSuccess] = useState(null);

  const shouldSendToUser = (recepient) => recepient === EMAIL;
  const shouldSendToCohort = (recepient) => recepient === MIXPANEL_COHORT_ID;

  const handleSendMessage = async (values, actions) => {
    const { messageId, email, mixpanelCohortId, recepient } = values;
    const params = shouldSendToUser(recepient)
      ? { email }
      : { mixpanelCohortId };

    actions.setSubmitting(true);
    await onSendMessage(messageId, params);

    setSuccess({ message: 'The message was successfully sent' });
    actions.resetForm();
    actions.setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSendMessage,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
  });

  const { handleSubmit, setFieldValue, values, errors, setErrors } = formik;

  const handleChange = ({ target }) => {
    const { id, value } = target;
    if (id === FORM_FIELDS.recepient) resetRecepient();
    setFieldValue(id, value);
  };

  const resetRecepient = () => {
    setFieldValue(FORM_FIELDS.email, '');
    setFieldValue(FORM_FIELDS.mixpanelCohortId, '');
  };

  useEffect(() => {
    if (!isNil(apiErrors)) {
      setErrors(apiErrorsToFormik(apiErrors));
    }
    return () => {
      setErrors({});
    };
  }, [apiErrors]);

  return (
    <form onSubmit={handleSubmit}>
      <FormHeader title="Send Message">
        <input type="submit" value="Send" />
      </FormHeader>
      <div className="form-fields">
        {!isNil(success) && (
          <Flash response={success} type={FLASH_TYPES.success} />
        )}
        <Group>
          <Row className="-with-bottom-space">
            <Column>
              <MessageSelect
                value={values.messageId}
                onChange={handleChange}
                messages={messages}
                isFulfilled={isFulfilled}
                errors={errors}
              />
            </Column>
            <Column>
              <RecepientSelect
                value={values.recepient}
                onChange={handleChange}
                errors={errors}
              />
            </Column>
          </Row>
          <Row>
            <Column></Column>
            <Column>
              {shouldSendToUser(values.recepient) && (
                <EmailInput
                  value={values.email}
                  onChange={handleChange}
                  errors={errors}
                />
              )}
              {shouldSendToCohort(values.recepient) && (
                <TextInput
                  id="mixpanelCohortId"
                  value={values.mixpanelCohortId}
                  label={'Mixpanel Cohort ID'}
                  onChange={handleChange}
                  errors={errors}
                />
              )}
            </Column>
          </Row>
        </Group>
      </div>
    </form>
  );
};

export default SendMessageForm;

SendMessageForm.propTypes = {
  messages: PropTypes.arrayOf(MessagePresenter.shape()),
  loadingStatus: PropTypes.string.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  apiErrors: PropTypes.shape(),
};

SendMessageForm.defaultProps = {
  messages: [],
  apiErrors: {},
};
